import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { fieldErrors, useAsync, useDebounced, useErrorMessage } from '../lib/hooks';
import { useI18n, useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { useToast } from '../components/Toast';
import { Icon } from '../components/Icon';
import {
  ActiveBadge, Checkbox, ConfirmDialog, EmptyState, ErrorAlert, LoadingRow, Modal,
  Pagination, SearchInput, SelectField, TextField,
} from '../components/ui';
import { PasswordPrompt } from './Security';
import { formatDate } from '@shared/format';
import { LOCALES, type Locale, type User } from '@shared/types';

const PER_PAGE = 20;
const LOCALE_LABELS: Record<Locale, string> = { es: 'Español', en: 'English' };

type Row = User & { invoiceCount: number; twoFactorEnabled: boolean };

export function Users() {
  const t = useT();
  const { locale } = useI18n();
  const { user: currentUser, refresh } = useSession();
  const toast = useToast();
  const toMessage = useErrorMessage();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Row | 'new'>();
  const [changingPassword, setChangingPassword] = useState<Row>();
  const [pendingDelete, setPendingDelete] = useState<Row>();
  const [resetting2fa, setResetting2fa] = useState<Row>();
  const [deleting, setDeleting] = useState(false);

  const q = useDebounced(search);
  const { data, loading, error, reload } = useAsync(
    () => api.users({ q, page, perPage: PER_PAGE }), [q, page]);

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.deleteUser(pendingDelete.id);
      toast.success(t('common.deleted'));
      setPendingDelete(undefined);
      reload();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div><h1 className="page-head__title">{t('users.title')}</h1></div>
        <div className="page-head__actions">
          <button type="button" className="btn btn--primary" onClick={() => setEditing('new')}>
            <Icon name="plus" />{t('users.new')}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card__head">
          <div className="toolbar" style={{ flex: 1 }}>
            <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }}
              placeholder={t('users.searchPlaceholder')} />
            {loading && data && <span className="spinner" />}
          </div>
        </div>

        {error ? <div className="card__body"><ErrorAlert message={error} onRetry={reload} /></div>
          : loading && !data ? <LoadingRow />
          : data && data.items.length === 0 ? (
            <EmptyState icon={q ? 'search' : 'shield'} title={t('common.noResults')}
              hint={t('common.noResultsHint')} />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('users.field.firstName')}</th>
                    <th className="shrink">{t('users.field.username')}</th>
                    <th>{t('users.field.email')}</th>
                    <th className="shrink num">{t('invoices.title')}</th>
                    <th className="shrink">{t('users.field.locale')}</th>
                    <th className="shrink">{t('twofactor.title')}</th>
                    <th className="shrink">{t('common.status')}</th>
                    <th className="shrink">{t('common.created')}</th>
                    <th className="shrink" />
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <span className="table__primary">{user.firstName} {user.lastName}</span>
                        {user.id === currentUser?.id && (
                          <span className="badge badge--accent" style={{ marginInlineStart: 7 }}>
                            {t('users.you')}
                          </span>
                        )}
                      </td>
                      <td className="shrink mono">{user.username}</td>
                      <td className="table__meta truncate" style={{ maxWidth: 240 }}>{user.email}</td>
                      <td className="shrink num">{user.invoiceCount}</td>
                      <td className="shrink table__meta">{LOCALE_LABELS[user.locale]}</td>
                      <td className="shrink">
                        <span className={`badge ${user.twoFactorEnabled ? 'badge--paid' : 'badge--pending'}`}>
                          {user.twoFactorEnabled ? t('common.active') : t('twofactor.notConfigured')}
                        </span>
                      </td>
                      <td className="shrink"><ActiveBadge active={user.isActive} /></td>
                      <td className="shrink table__meta nowrap">{formatDate(user.createdAt, locale)}</td>
                      <td className="shrink">
                        <div className="table__actions">
                          <button type="button" className="btn btn--ghost btn--icon"
                            title={t('users.changePassword')} aria-label={t('users.changePassword')}
                            onClick={() => setChangingPassword(user)}>
                            <Icon name="key" />
                          </button>
                          <button type="button" className="btn btn--ghost btn--icon"
                            title={t('twofactor.adminReset')} aria-label={t('twofactor.adminReset')}
                            disabled={!user.twoFactorEnabled}
                            onClick={() => setResetting2fa(user)}>
                            <Icon name="shield" />
                          </button>
                          <button type="button" className="btn btn--ghost btn--icon"
                            title={t('common.edit')} aria-label={t('common.edit')}
                            onClick={() => setEditing(user)}>
                            <Icon name="edit" />
                          </button>
                          <button type="button" className="btn btn--ghost btn--icon"
                            title={t('common.delete')} aria-label={t('common.delete')}
                            disabled={user.id === currentUser?.id}
                            onClick={() => setPendingDelete(user)}>
                            <Icon name="trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        {data && data.items.length > 0 && (
          <div className="card__foot">
            <Pagination page={data.page} totalPages={data.totalPages} total={data.total}
              perPage={data.perPage} onChange={setPage} />
          </div>
        )}
      </div>

      {editing && (
        <Modal
          title={editing === 'new' ? t('users.new') : t('users.edit')}
          onClose={() => setEditing(undefined)}
        >
          <UserForm
            user={editing === 'new' ? undefined : editing}
            isSelf={editing !== 'new' && editing.id === currentUser?.id}
            onCancel={() => setEditing(undefined)}
            onSaved={(created) => {
              toast.success(t(created ? 'users.created' : 'users.updated'));
              setEditing(undefined);
              reload();
              // Editing yourself can change the language the shell renders in.
              void refresh();
            }}
          />
        </Modal>
      )}

      {changingPassword && (
        <Modal title={t('users.changePassword')} onClose={() => setChangingPassword(undefined)}>
          <PasswordForm
            user={changingPassword}
            onCancel={() => setChangingPassword(undefined)}
            onSaved={() => {
              toast.success(t('users.passwordChanged'));
              setChangingPassword(undefined);
            }}
          />
        </Modal>
      )}

      {resetting2fa && (
        <PasswordPrompt
          title={t('twofactor.adminReset')}
          hint={t('twofactor.adminResetConfirm', {
            name: `${resetting2fa.firstName} ${resetting2fa.lastName}`,
          })}
          onCancel={() => setResetting2fa(undefined)}
          onConfirm={async (password) => {
            const result = await api.resetUserTwoFactor(resetting2fa.id, password);
            toast.success(t('twofactor.adminResetDone', { name: result.name }));
            setResetting2fa(undefined);
            reload();
          }}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          title={t('common.delete')}
          message={
            pendingDelete.invoiceCount > 0
              ? t('users.deleteBlocked', {
                  name: `${pendingDelete.firstName} ${pendingDelete.lastName}`,
                  count: pendingDelete.invoiceCount,
                })
              : t('users.deleteConfirm', { name: `${pendingDelete.firstName} ${pendingDelete.lastName}` })
          }
          confirmLabel={t('common.delete')}
          danger
          busy={deleting}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setPendingDelete(undefined)}
        />
      )}
    </>
  );
}

// ── user form ───────────────────────────────────────────────────────────────
function UserForm({ user, isSelf, onSaved, onCancel }: {
  user?: Row;
  isSelf: boolean;
  onSaved: (created: boolean) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const toMessage = useErrorMessage();

  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [username, setUsername] = useState(user?.username ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [userLocale, setUserLocale] = useState<Locale>(user?.locale ?? 'es');
  const [isActive, setIsActive] = useState(user?.isActive ?? true);
  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    setFormError(undefined);
    const base = {
      firstName: firstName.trim(), lastName: lastName.trim(),
      username: username.trim(), email: email.trim(), locale: userLocale, isActive,
    };
    try {
      if (user) await api.updateUser(user.id, base);
      else await api.createUser({ ...base, password, passwordRepeat });
      onSaved(!user);
    } catch (err) {
      const fields = fieldErrors(err);
      setErrors(fields);
      if (Object.keys(fields).length === 0) setFormError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="stack">
      {formError && <ErrorAlert message={formError} />}

      <div className="form-grid form-grid--2">
        <TextField label={t('users.field.firstName')} required autoFocus
          value={firstName} error={errors.firstName} onChange={(e) => setFirstName(e.target.value)} />
        <TextField label={t('users.field.lastName')} required
          value={lastName} error={errors.lastName} onChange={(e) => setLastName(e.target.value)} />
        <TextField label={t('users.field.username')} required autoComplete="off"
          value={username} error={errors.username} onChange={(e) => setUsername(e.target.value)} />
        <TextField label={t('users.field.email')} required type="email" autoComplete="off"
          value={email} error={errors.email} onChange={(e) => setEmail(e.target.value)} />
        <SelectField label={t('users.field.locale')} value={userLocale}
          onChange={(e) => setUserLocale(e.target.value as Locale)}>
          {LOCALES.map((code) => (
            <option key={code} value={code}>{LOCALE_LABELS[code]}</option>
          ))}
        </SelectField>

        {!user && (
          <>
            <TextField label={t('users.field.password')} required type="password" autoComplete="new-password"
              hint={t('users.passwordHint')}
              value={password} error={errors.password} onChange={(e) => setPassword(e.target.value)} />
            <TextField label={t('users.field.passwordRepeat')} required type="password"
              autoComplete="new-password"
              value={passwordRepeat} error={errors.passwordRepeat}
              onChange={(e) => setPasswordRepeat(e.target.value)} />
          </>
        )}
      </div>

      <Checkbox label={t('common.active')} checked={isActive}
        onChange={isSelf ? () => undefined : setIsActive} />
      {isSelf && <div className="field__hint">{t('users.deleteSelf')}</div>}

      <div className="row row--end">
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy && <span className="spinner" />}
          {busy ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  );
}

// ── password form ───────────────────────────────────────────────────────────
function PasswordForm({ user, onSaved, onCancel }: {
  user: Row;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const t = useT();
  const toMessage = useErrorMessage();
  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    setFormError(undefined);
    try {
      await api.setUserPassword(user.id, password, passwordRepeat);
      onSaved();
    } catch (err) {
      const fields = fieldErrors(err);
      setErrors(fields);
      if (Object.keys(fields).length === 0) setFormError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="stack">
      {formError && <ErrorAlert message={formError} />}
      <p className="muted">{user.firstName} {user.lastName} · <span className="mono">{user.username}</span></p>

      <TextField label={t('users.field.password')} required type="password" autoFocus
        autoComplete="new-password" hint={t('users.passwordHint')}
        value={password} error={errors.password} onChange={(e) => setPassword(e.target.value)} />
      <TextField label={t('users.field.passwordRepeat')} required type="password"
        autoComplete="new-password"
        value={passwordRepeat} error={errors.passwordRepeat}
        onChange={(e) => setPasswordRepeat(e.target.value)} />

      <div className="row row--end">
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy && <span className="spinner" />}
          {t('common.save')}
        </button>
      </div>
    </form>
  );
}
